import { useState, Fragment, useEffect } from "react";
import { useNavigate } from "react-router-dom";

//Material UI
import { Fab, Grid, Typography } from "@mui/material";
import { Replay, Save } from "@mui/icons-material";
import GetAppIcon from '@mui/icons-material/GetApp';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogActions';

//Hooks
import useNotify from "../../../hooks/notify";
import useStockControl from "../../../hooks/stockControls/useStockControl";
import { useBlocker } from '../../../hooks/useBlocker/useBlocker';

//Components
import Modal from '../../../components/modal';
import StockControlDetailsTable from "./detail";
import { Spinner } from "@arredo/reactjs-ui";
import ModalConfirm from "../../../components/modal/confirm";
import ProductCategoryAutocomplete from '../../../components/productCategoryAutocomplete';

//Others
import useStyles from '../styles'
import { stockControlStates } from "../../../models/StockControlEntity";
import StockControlService from "../../../services/stockControlService";
import useUser from "../../../hooks/user/useUser";
import { FabContainer } from "@arredo/reactjs-ui";

const StockControlEdition = ({ warehouseId, stockControlId, branchCode }) => {
    const { classes } = useStyles();

    //States
    const [saving, setSaving] = useState();
    const [reChecked, setReChecked] = useState(false);
    const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
    const [categoryModalOpen, setCategoryModalOpen] = useState(true);
    const [categories, setCategories] = useState([])
    const [shouldBlockNavigation, setShouldBlockNavigation] = useState(true);

    //Hooks
    const user = useUser();
    const push = useNavigate();
    const { Notify, variants } = useNotify();
    const {
        stockControl, loading, addProduct, addProductMassive, removeProduct,
        changeProduct, editProduct, reCheck, save, searchProductsByCategories
    } = useStockControl(warehouseId, Number(stockControlId) || null, branchCode, user.email);

    useEffect(() => {
        window.onbeforeunload = shouldBlockNavigation
            ? () => true
            : undefined;

        return () => {
            window.onbeforeunload = undefined;
        }
    }, [shouldBlockNavigation]);

    useBlocker((tx) => {
        if (shouldBlockNavigation && !window.confirm("¿Estás seguro? Si te vas perderás todo el progreso realizado hasta ahora.")) {
            return;
        }
        tx.retry();
    }, shouldBlockNavigation);

    const handleAddProduct = async (refId) => {
        if (!(await addProduct(refId)))
            Notify("No se encontró el producto que quiere agregar.", variants.INFO);
    }

    const handleAddProductsMassive = async (refIds) => {
        if (!(await addProductMassive(refIds)))
            Notify("Error al intentar agregar productos.", variants.INFO);
    }

    const handleSetProductsMassive = async (refIds) => {
        if (!(await addProductMassive(refIds, true)))
            Notify("Error al intentar agregar productos.", variants.INFO);
    }

    const handleRemoveProduct = (refId) => {
        if (!removeProduct(refId))
            Notify("No se encontro el producto que quiere eliminar.", variants.INFO);
    }

    const handleSaveEditProduct = (refId, payload) => {
        const method = refId === payload.refId ? editProduct : changeProduct
        method(refId, payload);
    }

    const handleReCheck = (event) => {
        setReChecked(true);
        reCheck();
    }

    const handleDowload = async () => {
        try {
            const blob = await StockControlService.BuildExcel(stockControl.details);

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.style.display = "none";
            a.href = url;
            a.download = `Diferencias_${new Date().toLocaleDateString()}.xlsx`
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            Notify(`Error al descargar las diferencias`, variants.ERROR);
            console.error(e);
        }
    }

    const handleClickSave = async (event) => {
        if (stockControl.details.some(d => d.difference !== 0))
            return setConfirmSaveOpen(true);
        handleSave();
    }

    const handleSave = async () => {
        setSaving(true);
        setShouldBlockNavigation(false);
        save()
            .then(() => {
                Notify(`Control de Inventario ${stockControl.id ? 'actualizado' : 'creado'} exitosamente.`, variants.SUCCESS);
                push('/stockControls');
            })
            .catch(err => {
                if (err) console.error(err);
                Notify("Ocurrio un error al intentar guardar el control de inventario.", variants.ERROR);
            })
            .finally(() => {
                setShouldBlockNavigation(true);
                setSaving(false);
                setConfirmSaveOpen(false);
            });
    }

    const handleModalSearch = async () => {
        const results = await searchProductsByCategories(categories);

        if (!results?.length) {
            Notify("No se encontraron productos para esa familia", variants.INFO);
            return;
        }

        await addProductMassive(results?.map(r => r.refId), true, 0);
        setCategoryModalOpen(false);
    }

    const handleProductCategoryAutocomplete = (values) => {
        setCategories(values);
    }

    return (
        <>
            {
                (loading || saving) &&
                <Spinner lockScreen />
            }
            <Modal
                open={categoryModalOpen}
                disableAutoFocus
                disableEnforceFocus
                disableEscapeKeyDown
                title="Seleccionar familia de productos"
                customButtons={() =>
                    <DialogContent>
                        <Button
                            onClick={() => push('/stockControls')}
                            color="primary"
                            size="small"
                        >
                            Volver
                        </Button>
                        <Button
                            onClick={handleModalSearch}
                            disabled={!categories?.length}
                            variant="contained"
                            color="primary"
                            size="small"
                        >
                            Aceptar
                        </Button>
                    </DialogContent>
                }
            >
                <ProductCategoryAutocomplete onChange={handleProductCategoryAutocomplete} />
            </Modal>
            {
                confirmSaveOpen &&
                <ModalConfirm
                    open={confirmSaveOpen}
                    onClose={() => setConfirmSaveOpen(false)}
                    onCancel={() => setConfirmSaveOpen(false)}
                    onAccept={() => handleSave()}
                    message="Hay productos que presentan diferencias. ¿Desea guardar de todas formas?"
                />
            }
            <Grid container direction='column' spacing={2}>
                {
                    !stockControl.id &&
                    <Grid>
                        <Typography variant='h4' component='h2'>
                            Nuevo control de inventario
                        </Typography>
                    </Grid>
                }
                <Grid>
                    <StockControlDetailsTable
                        details={stockControl.details}
                        onRemove={stockControl.state !== stockControlStates.adjusted ? handleRemoveProduct : null}
                        onAdd={handleAddProduct}
                        onChange={stockControl.state !== stockControlStates.adjusted ? handleSaveEditProduct : null}
                        onAddMassive={handleAddProductsMassive}
                        onSetMassive={handleSetProductsMassive}
                        state={stockControl.id ? stockControl.state : null}
                    />
                </Grid>
            </Grid>
            {
                stockControl.state !== stockControlStates.adjusted &&
                <FabContainer>
                    {
                        reChecked &&
                        <Fab
                            color='primary'
                            variant="extended"
                            className={classes.fab}
                            onClick={handleDowload}
                        >
                            <GetAppIcon className={classes.extendedIcon} />
                            Descargar diferencias
                        </Fab>
                    }
                    <Fab
                        color='primary'
                        variant="extended"
                        className={classes.fab}
                        onClick={handleReCheck}
                    >
                        <Replay className={classes.extendedIcon} />
                        Recontrol
                    </Fab>
                    <Fab
                        disabled={!reChecked}
                        color='primary'
                        variant="extended"
                        className={classes.fab}
                        onClick={handleClickSave}
                    >
                        <Save className={classes.extendedIcon} />
                        Guardar
                    </Fab>
                </FabContainer>
            }
        </>
    );
}

export default StockControlEdition;
